import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { differenceInCalendarDays } from "date-fns";
import { SessionTenantInfo } from "@api/core.models";
import { TenantDebtLock } from "@vadmin/account/auth";
import { AlertService } from "@vui/legacy-alert/service";
import { Alert } from "@vui/legacy-alert/model";
import { isVindiPaymentGateway } from "../payment-gateway.util";

export const DAYS_OVERDUE_BEFORE_BLOCKING = 30;

export const DAYS_OVERDUE_BEFORE_WARNING = 10;

export const DAYS_OVERDUE_BEFORE_WARNING_CANNOT_BE_CLOSED = 25;

@Injectable()
export class VindiTenantDebtLock extends TenantDebtLock {
    private readonly _matDialog = inject(MatDialog);

    private readonly _alertService = inject(AlertService);

    private readonly _router = inject(Router);

    private readonly _transloco = inject(TranslocoService);

    enforce(tenantInfo: SessionTenantInfo): void {
        const activeBilling = tenantInfo.subscriptionInfo?.activeBilling;
        if (
            !activeBilling ||
            !isVindiPaymentGateway(tenantInfo.paymentGateway) ||
            !abp.setting.getBoolean("System.Payments.EnableDebtLock")
        ) {
            return;
        }

        const today = new Date();
        const invoices = Object.entries(activeBilling).map(([billingKey, dueDate]) => ({
            billingKey,
            daysUntilDue: differenceInCalendarDays(dueDate, today),
        }));
        if (invoices.length === 0) {
            return;
        }

        if (invoices.some(({ daysUntilDue }) => daysUntilDue <= -DAYS_OVERDUE_BEFORE_BLOCKING)) {
            void this._blockAccess();
            return;
        }

        invoices
            .filter(({ daysUntilDue }) => daysUntilDue <= -DAYS_OVERDUE_BEFORE_WARNING)
            .forEach(({ billingKey, daysUntilDue }) =>
                this._alertService.publishAlert(this._pendingPaymentAlert(billingKey, daysUntilDue))
            );
    }

    private async _blockAccess(): Promise<void> {
        // Loaded on demand: the dialog brings DetailsModule and its Material modules into the initial bundle (VS-1961)
        const { BlockedDialogComponent } = await import("../blocked-dialog");
        this._matDialog.open(BlockedDialogComponent, {
            disableClose: true,
            panelClass: "subscription-blocked-dialog",
        });
    }

    private _pendingPaymentAlert(billingKey: string, daysUntilDue: number): Alert {
        return {
            id: `subscription-billing-alert-${billingKey}`,
            type: "warning",
            title: this._transloco.translate("subscription-debt-lock.title"),
            description: this._transloco.translate("subscription-debt-lock.pending-payment", {
                days: DAYS_OVERDUE_BEFORE_BLOCKING + daysUntilDue,
            }),
            closeable: daysUntilDue > -DAYS_OVERDUE_BEFORE_WARNING_CANNOT_BE_CLOSED,
            isHidden: false,
            action: () => this._router.navigate(["/settings/info"], { queryParams: { tab: "billing" } }),
            showButton: true,
            buttonLabel: this._transloco.translate("subscription-debt-lock.settle-now"),
            buttonColor: "warn",
        };
    }
}
